/**
 * Absence API Types
 *
 * Transport DTOs (LC_*) for backend communication
 * and internal UI models for the absence domain.
 */

import type { APIExportFormat } from '@/types/fileExport.types';
import type { LC_PagedResult } from '@/api/shared/types';

// ── UI event constants ────────────────────────────────────────────────────

export const ABSENCE_SCOPE_LIST_REFRESH_EVENT = 'absence-scope-list-refresh' as const;

// ── Workday type constants ────────────────────────────────────────────────

/**
 * Códigos de tipo de jornada que consumen media jornada en vez de la jornada completa.
 * Deben coincidir con `AbsenceWorkdayTypeConstants` del backend.
 */
export const HALF_DAY_WORKDAY_TYPES: ReadonlySet<string> = new Set([
  'MEDIO_DIA_AM',
  'MEDIO_DIA_PM',
]);

// ============================================================================
// TRANSPORT DTOs (Backend)
// ============================================================================

/**
 * Request parameters for listing absences
 */
export interface LC_ListAbsencesIn {
  startDate: string;
  endDate: string;
  absenceType: AbsenceType;
  status: AbsenceStatus;
  page?: number;
  pageSize?: number;
  searchText?: string | null;
  orderBy?: string | null;
  orderDirection?: 'ASC' | 'DESC' | null;
}

/**
 * Request parameters for exporting absences
 */
export interface LC_ExportAbsencesIn {
  startDate: string;
  endDate: string;
  absenceType: AbsenceType;
  status: AbsenceStatus;
  format: APIExportFormat;
}

/**
 * Single absence item from backend
 */
export interface LC_AbsenceItemOut {
  id: string | null;
  type: string | null;
  description: string | null;
  startDate: string;
  endDate: string;
  status: string | null;
  createdAt: string;
  createdBy: string | null;
}

/**
 * Pagination metadata with totals by type
 */
export interface LC_AbsencePaginationMetadata {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  searchText: string | null;
  orderBy: string | null;
  orderDirection: string | null;
  totalMedicalLeaves: number;
  totalVacations: number;
  totalSpecialPermits: number;
  totalAdministrativePermits: number;
  totalFractionPermits: number;
  totalAbsences: number;
}

/**
 * Response from absence list endpoint
 */
export interface LC_ListAbsencesOut {
  data: LC_AbsenceItemOut[] | null;
  pagination: LC_AbsencePaginationMetadata;
}

// ============================================================================
// ENUMS / CONSTANTS
// ============================================================================

export const ABSENCE_TYPES = [
  'licencia',
  'vacacion',
  'permiso_especial',
  'permiso_administrativo',
  'permiso_fraccion',
  'falta',
  'todos',
] as const;

export type AbsenceType = typeof ABSENCE_TYPES[number];

export const ABSENCE_STATUSES = [
  'pendiente',
  'aprobado',
  'rechazado',
  'cancelado',
  'historico',
  'sin_justificar',
  'todos',
] as const;

export type AbsenceStatus = typeof ABSENCE_STATUSES[number];

/**
 * Select option interface
 */
export interface AbsenceSelectOption {
  value: string;
  label: string;
}

/**
 * Get type options for selectors (requires translation function)
 */
export const getAbsenceTypeOptions = (
  translateFn: (key: string) => string,
): AbsenceSelectOption[] => [
  { value: 'todos', label: translateFn('types.todos') },
  { value: 'licencia', label: translateFn('types.licencia') },
  { value: 'vacacion', label: translateFn('types.vacacion') },
  { value: 'permiso_especial', label: translateFn('types.permiso_especial') },
  { value: 'permiso_administrativo', label: translateFn('types.permiso_administrativo') },
  { value: 'permiso_fraccion', label: translateFn('types.permiso_fraccion') },
  { value: 'falta', label: translateFn('types.falta') },
];

/**
 * Get status options for selectors (requires translation function)
 */
export const getAbsenceStatusOptions = (
  translateFn: (key: string) => string,
): AbsenceSelectOption[] => [
  { value: 'todos', label: translateFn('statuses.todos') },
  { value: 'pendiente', label: translateFn('statuses.pendiente') },
  { value: 'aprobado', label: translateFn('statuses.aprobado') },
  { value: 'rechazado', label: translateFn('statuses.rechazado') },
  { value: 'cancelado', label: translateFn('statuses.cancelado') },
  { value: 'historico', label: translateFn('statuses.historico') },
  { value: 'sin_justificar', label: translateFn('statuses.sin_justificar') },
];

// ============================================================================
// UI MODELS
// ============================================================================

/**
 * Absence item for UI display
 */
export interface AbsenceItem {
  id: string;
  tipo: string;
  descripcion: string;
  fechaDesde: string;
  fechaHasta: string;
  estado: string;
  fechaCreacion: string;
  creadoPor: string;
}

/**
 * Summary totals for indicator cards
 */
export interface AbsenceTotals {
  licencias: number;
  vacaciones: number;
  permisosEspeciales: number;
  permisosAdministrativos: number;
  permisosFraccion: number;
  faltas: number;
}

/**
 * Paginated result for UI
 */
export interface AbsenceListResult {
  data: AbsenceItem[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  totals: AbsenceTotals;
}

// ============================================================================
// SCOPE TYPES (Management view — multi-employee)
// ============================================================================

/**
 * Request parameters for listing absences by scope (management view)
 */
export interface LC_ListAbsencesByScopeIn {
  startDate: string;
  endDate: string;
  absenceType: AbsenceType;
  status: AbsenceStatus;
  employeeIds?: string | null;
  page?: number;
  pageSize?: number;
  searchText?: string | null;
  orderBy?: string | null;
  orderDirection?: 'ASC' | 'DESC' | null;
}

/**
 * Request parameters for exporting the scope (management) absence listing to Excel.
 * Hybrid endpoint: backend responds 200+blob (sync) or 202+jobId (async) depending on volume.
 */
export interface LC_ExportAbsencesManagementIn {
  startDate: string;
  endDate: string;
  absenceType: AbsenceType;
  status: AbsenceStatus;
  employeeIds?: string | null;
  searchText?: string | null;
}

/**
 * Single absence item from backend scope endpoint (includes employee fields)
 */
export interface LC_AbsenceScopeItemOut extends LC_AbsenceItemOut {
  employeeId: number;
  employeeName: string;
  employeeTaxId: string;
  /** Código normalizado del tipo de ausentismo ('licencia' | 'vacacion' | ...), no la etiqueta
   * de `type`. */
  typeCode: string;
}

/**
 * Response from absence scope list endpoint
 */
export interface LC_ListAbsencesByScopeListOut {
  data: LC_AbsenceScopeItemOut[] | null;
  pagination: LC_AbsencePaginationMetadata;
}

/**
 * Absence scope item for UI display (adds employee columns)
 */
export interface AbsenceScopeItem extends AbsenceItem {
  empleado: string;
  rut: string;
  /** Código normalizado del tipo de ausentismo, usado para habilitar acciones de fila por tipo. */
  typeCode: AbsenceType;
  /** PK numérico del registro en su tabla origen; null cuando el id crudo no es numérico. */
  recordId: number | null;
}

/**
 * Paginated scope result for UI
 */
export interface AbsenceScopeListResult extends AbsenceListResult {
  data: AbsenceScopeItem[];
}

// ============================================================================
// Medical Leave (Licencia médica)
// ============================================================================

export interface LC_GetEmployeesForAbsenceIn {
  searchText?: string;
  page: number;
  pageSize: number;
}

export interface LC_EmployeeForAbsenceItemOut {
  id: number;
  employeeTaxIdentificationNumber: string;
  fullName: string;
  companyName: string;
  isBlocked: boolean;
}

export type LC_GetEmployeesForAbsenceOut = LC_PagedResult<LC_EmployeeForAbsenceItemOut>;

/** Modelo de dominio del colaborador seleccionable para crear una ausencia */
export interface EmployeeForAbsence {
  id: number;
  employeeTaxIdentificationNumber: string;
  fullName: string;
  companyName: string;
  isBlocked: boolean;
}

export interface EmployeesForAbsenceResult {
  data: EmployeeForAbsence[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
}

export interface LC_MedicalLeaveTypeItemOut {
  id: number;
  name: string;
}

export type LC_GetMedicalLeaveTypesOut = LC_MedicalLeaveTypeItemOut[];

export interface LC_CreateMedicalLeaveIn {
  employeeTargetId: number;
  leaveTypeId: number;
  startDate: string;  // yyyy-MM-dd
  endDate: string;    // yyyy-MM-dd
  returnDate?: string; // yyyy-MM-dd, calculado por el sistema; el servidor lo recalcula igual
  folio: string;
  workdayType: string;
  dayCount: number;
}

export interface LC_CreateMedicalLeaveOut {
  id: number;
  leaveTypeId: number;
  leaveTypeName: string;
  startDate: string;
  endDate: string;
  returnDate: string;
  status: string;
  createdAt: string;
}

/** Previsualización de fecha de término y fecha de retorno mientras se edita el formulario. */
export interface LC_CalculateAbsenceDatesIn {
  employeeTargetId: number;
  startDate: string; // yyyy-MM-dd
  dayCount: number;
  workdayType?: string;
}

export interface LC_CalculateAbsenceDatesOut {
  endDate: string; // yyyy-MM-dd
  returnDate: string; // yyyy-MM-dd
}

/** Detalle de una licencia médica para el panel de ver/editar (ASIS-5294). */
export interface LC_GetMedicalLeaveDetailOut {
  id: number;
  employeeTargetId: number;
  employeeName: string;
  employeeTaxId: string;
  leaveTypeId: number;
  leaveTypeName: string;
  folio: string | null;
  workdayType: string;
  workdayTypeLabel: string;
  dayCount: number;
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
  returnDate: string | null; // yyyy-MM-dd
  /** Administrado desde Remuneraciones (contrato activo con sincronización de asistencia) → solo
   * lectura. */
  isManagedByPayroll: boolean;
}

/**
 * Entrada para editar una licencia médica existente. Deliberadamente no incluye
 * employeeTargetId, endDate ni returnDate — el colaborador no es editable y el servidor
 * recalcula esos valores de forma autoritativa.
 */
export interface LC_UpdateMedicalLeaveIn {
  leaveTypeId: number;
  startDate: string; // yyyy-MM-dd
  folio: string;
  workdayType: string;
  dayCount: number;
}

export interface LC_UpdateMedicalLeaveOut {
  id: number;
  leaveTypeId: number;
  leaveTypeName: string;
  startDate: string;
  endDate: string;
  returnDate: string;
  updatedAt: string;
}

/** Modelo UI del detalle de una licencia médica. */
export interface MedicalLeaveDetail {
  id: number;
  employeeTargetId: number;
  employeeName: string;
  employeeTaxId: string;
  leaveTypeId: number;
  folio: string;
  workdayType: string;
  dayCount: number;
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
  returnDate: string; // yyyy-MM-dd, '' si el backend no la envía
  isManagedByPayroll: boolean;
}

export interface LC_AbsenceWorkdayTypeItemOut {
  code: string;
  label: string;
}

// El backend implementa ICatalogResponse en LC_GetAbsenceWorkdayTypesListOut, que
// ApiEnvelopeFactory aplana a un array desnudo en `data` (mismo patrón que
// LC_GetMedicalLeaveTypesOut arriba) — nunca llega envuelto en { workdayTypes: [...] }.
export type LC_GetAbsenceWorkdayTypesOut = LC_AbsenceWorkdayTypeItemOut[];

// ============================================================================
// Special Leave (Permiso especial)
// ============================================================================

export interface LC_SpecialLeaveTypeItemOut {
  id: number;
  name: string;
}

// Igual que LC_GetAbsenceWorkdayTypesOut: el backend aplana LC_GetSpecialLeaveTypesListOut
// (ICatalogResponse) a un array desnudo en `data`.
export type LC_GetSpecialLeaveTypesOut = LC_SpecialLeaveTypeItemOut[];

export interface LC_CreateSpecialLeaveIn {
  employeeTargetId: number;
  specialLeaveTypeId: number;
  startDate: string; // yyyy-MM-dd
  workdayType: string;
  dayCount: number;
  detail?: string;
}

export interface LC_CreateSpecialLeaveOut {
  id: number;
  specialLeaveTypeId: number;
  specialLeaveTypeName: string;
  startDate: string;
  endDate: string;
  returnDate: string;
  status: string;
  createdAt: string;
}

// ============================================================================
// Vacation (Vacaciones)
// ============================================================================

/**
 * Entrada para crear un período de vacaciones.
 * No incluye endDate ni returnDate: el servidor las calcula de forma autoritativa contando
 * únicamente días hábiles, igual que hace el endpoint de previsualización.
 */
export interface LC_CreateVacationIn {
  employeeTargetId: number;
  startDate: string; // yyyy-MM-dd
  workdayType: string;
  dayCount: number;
  detail?: string;
}

export interface LC_CreateVacationOut {
  id: number;
  startDate: string;
  endDate: string;
  returnDate: string;
  workdayType: string;
  dayCount: number;
  status: string;
  createdAt: string;
}

/** Previsualización de fecha de término y fecha de retorno mientras se edita el formulario. */
export interface LC_CalculateVacationDatesIn {
  employeeTargetId: number;
  startDate: string; // yyyy-MM-dd
  dayCount: number;
  workdayType: string;
}

export interface LC_CalculateVacationDatesOut {
  endDate: string; // yyyy-MM-dd
  returnDate: string; // yyyy-MM-dd
}

// ============================================================================
// Fractional Leave Assignment (Permiso fracción)
// ============================================================================

export interface LC_FractionalLeaveTypeItemOut {
  id: number;
  name: string;
}

// Igual que LC_GetSpecialLeaveTypesOut: el backend aplana LC_GetFractionalLeaveTypesListOut
// (ICatalogResponse) a un array desnudo en `data`.
export type LC_GetFractionalLeaveTypesOut = LC_FractionalLeaveTypeItemOut[];

export interface LC_GetCalculatedLateHoursOut {
  decimalHours: number;
  formattedHours: string;
}

export interface LC_CreateFractionalLeaveAssignmentIn {
  employeeTargetId: number;
  fractionalLeaveTypeId: number;
  startDate: string; // yyyy-MM-dd
  hoursToJustify: string; // HH:mm
  detail?: string;
}

export interface LC_CreateFractionalLeaveAssignmentOut {
  id: number;
  employeeTargetId: number;
  fractionalLeaveTypeId: number;
  fractionalLeaveTypeName: string;
  startDate: string;
  hoursToJustify: string;
  createdAt: string;
}
